import React, { useState, useCallback } from 'react';
import { Button } from '@pagespeedify/ui';
import { Input } from '@pagespeedify/ui';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@pagespeedify/ui';
import { Loader2 } from 'lucide-react';

interface CreateTeamFormProps {
  onSubmit: (teamName: string) => void;
  isCreating: boolean;
}

// Validation function
const validateTeamName = (name: string): string | null => {
  const trimmedName = name.trim();
  if (!trimmedName) {
    return 'Team name is required.';
  }
  if (trimmedName.length > 12) {
    return 'Team name cannot exceed 12 characters.';
  }
  if (!/^[a-zA-Z0-9]+$/.test(trimmedName)) {
    return 'Team name must be alphanumeric (letters and numbers only).';
  }
  return null; // No error
};

export const CreateTeamForm: React.FC<CreateTeamFormProps> = ({ onSubmit, isCreating }) => {
  const [teamName, setTeamName] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newName = event.target.value;
    setTeamName(newName);
    // Validate on change
    setValidationError(validateTeamName(newName));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const error = validateTeamName(teamName);
    setValidationError(error); // Ensure error state is up-to-date on submit attempt

    if (!error) {
      onSubmit(teamName.trim());
    }
  };

  // Determine if button should be disabled
  const isSubmitDisabled = isCreating || !!validationError || !teamName.trim();

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>Create Your Team</CardTitle>
        <CardDescription>You don't have a team yet. Create one to start adding members.</CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-2"> {/* Added space-y for error message */}
          <Input
            id="teamName"
            placeholder="Enter team name (max 12, alphanumeric)" // Updated placeholder
            value={teamName}
            onChange={handleInputChange} // Use updated handler
            disabled={isCreating}
            required
            aria-label="Team Name"
            aria-invalid={!!validationError} // Indicate invalid state for accessibility
            aria-describedby={validationError ? "teamName-error" : undefined} // Link error message
            maxLength={12} // Set max input length to 12
          />
          {/* Display validation error */}
          {validationError && (
            <p id="teamName-error" className="text-sm text-red-600">
              {validationError}
            </p>
          )}
        </CardContent>
        <CardFooter>
          <Button type="submit" disabled={isSubmitDisabled} className="w-full">
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Team'
            )}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};